import { useEffect, useState } from 'react'

import { readMediaObjectUrl } from '@/lib/api/client/inventory'
import { unavailableReasonText } from '@/lib/warehouse/labels'

const MAX_CACHED_MEDIA = 300
const MAX_CONCURRENT_MEDIA_READS = 6

interface MediaCacheEntry {
  mediaUrl: string
  objectUrl: string | null
  promise: Promise<string>
  resolve: (objectUrl: string) => void
  reject: (reason: unknown) => void
  references: number
  lastReleasedAt: number
  controller: AbortController
  queued: boolean
  started: boolean
  cancelled: boolean
  failed: boolean
}

const mediaCache = new Map<string, MediaCacheEntry>()
const mediaQueue: MediaCacheEntry[] = []
let activeMediaReads = 0
let releaseSequence = 0

function createDeferred(): Pick<MediaCacheEntry, 'promise' | 'resolve' | 'reject'> {
  let resolvePromise: ((objectUrl: string) => void) | undefined
  let rejectPromise: ((reason: unknown) => void) | undefined
  const promise = new Promise<string>((resolve, reject) => {
    resolvePromise = resolve
    rejectPromise = reject
  })
  if (resolvePromise === undefined || rejectPromise === undefined) {
    throw new Error('Media cache promise was not initialized')
  }
  return { promise, resolve: resolvePromise, reject: rejectPromise }
}

function revokeObjectUrl(objectUrl: string): void {
  URL.revokeObjectURL(objectUrl)
}

function removeQueuedEntry(entry: MediaCacheEntry): void {
  const index = mediaQueue.indexOf(entry)
  if (index >= 0) mediaQueue.splice(index, 1)
}

function evictEntry(entry: MediaCacheEntry): void {
  if (mediaCache.get(entry.mediaUrl) !== entry || entry.references > 0) return
  mediaCache.delete(entry.mediaUrl)
  entry.cancelled = true
  if (entry.queued) removeQueuedEntry(entry)
  if (!entry.started) entry.controller.abort()
  if (entry.objectUrl !== null) {
    revokeObjectUrl(entry.objectUrl)
    entry.objectUrl = null
  }
}

function evictReleasedEntries(): void {
  while (mediaCache.size > MAX_CACHED_MEDIA) {
    const candidate = [...mediaCache.values()]
      .filter((entry) => entry.references === 0)
      .sort((left, right) => left.lastReleasedAt - right.lastReleasedAt)[0]
    if (candidate === undefined) return
    evictEntry(candidate)
  }
}

function pumpMediaQueue(): void {
  while (activeMediaReads < MAX_CONCURRENT_MEDIA_READS) {
    const entry = mediaQueue.shift()
    if (entry === undefined) return
    entry.queued = false
    if (entry.cancelled || mediaCache.get(entry.mediaUrl) !== entry) continue
    entry.started = true
    activeMediaReads += 1
    void readMediaObjectUrl(entry.mediaUrl, entry.controller.signal)
      .then((objectUrl) => {
        if (entry.cancelled || mediaCache.get(entry.mediaUrl) !== entry) return
        entry.objectUrl = objectUrl
        entry.resolve(objectUrl)
      })
      .catch((reason: unknown) => {
        entry.failed = true
        entry.reject(reason)
        if (mediaCache.get(entry.mediaUrl) === entry && entry.references === 0) {
          mediaCache.delete(entry.mediaUrl)
        }
      })
      .finally(() => {
        activeMediaReads -= 1
        evictReleasedEntries()
        pumpMediaQueue()
      })
  }
}

function createMediaCacheEntry(mediaUrl: string): MediaCacheEntry {
  const deferred = createDeferred()
  const entry: MediaCacheEntry = {
    mediaUrl,
    objectUrl: null,
    promise: deferred.promise,
    resolve: deferred.resolve,
    reject: deferred.reject,
    references: 0,
    lastReleasedAt: Number.POSITIVE_INFINITY,
    controller: new AbortController(),
    queued: true,
    started: false,
    cancelled: false,
    failed: false,
  }
  mediaCache.set(mediaUrl, entry)
  mediaQueue.push(entry)
  return entry
}

function acquireMediaUrl(mediaUrl: string): MediaCacheEntry {
  const entry = mediaCache.get(mediaUrl) ?? createMediaCacheEntry(mediaUrl)
  entry.references += 1
  entry.lastReleasedAt = Number.POSITIVE_INFINITY
  evictReleasedEntries()
  pumpMediaQueue()
  return entry
}

function releaseMediaUrl(entry: MediaCacheEntry): void {
  if (entry.references === 0) return
  entry.references -= 1
  if (entry.references === 0) {
    if (entry.failed && mediaCache.get(entry.mediaUrl) === entry) {
      mediaCache.delete(entry.mediaUrl)
      return
    }
    entry.lastReleasedAt = ++releaseSequence
    evictReleasedEntries()
  }
}

/**
 * One Warehouse row's product tile.
 *
 * `mediaUrl` is this product's own verified primary file and nothing else; the
 * server publishes it only for a downloaded row whose evidence object resolved.
 * When it is null the server names why instead, and the empty square carries
 * that reason rather than pretending the picture is still on its way.
 */
export function WarehouseThumbnail({
  mediaUrl,
  label,
  unavailableReason = null,
  size = 'default',
}: {
  mediaUrl: string | null
  label: string
  unavailableReason?: string | null
  size?: 'default' | 'large'
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(mediaUrl !== null)

  useEffect(() => {
    let mounted = true
    setObjectUrl(null)
    setLoading(mediaUrl !== null)
    if (!mediaUrl) {
      return () => { mounted = false }
    }

    const entry = acquireMediaUrl(mediaUrl)
    if (entry.objectUrl !== null) {
      setObjectUrl(entry.objectUrl)
      setLoading(false)
    } else {
      entry.promise
        .then((url) => {
          if (mounted) {
            setObjectUrl(url)
            setLoading(false)
          }
        })
        .catch(() => {
          if (mounted) {
            setObjectUrl(null)
            setLoading(false)
          }
        })
    }
    return () => {
      mounted = false
      releaseMediaUrl(entry)
    }
  }, [mediaUrl])

  const empty = objectUrl === null && !loading
  return (
    <span
      data-testid="warehouse-thumbnail"
      data-media-state={objectUrl ? 'available' : loading ? 'loading' : 'empty'}
      data-reason={empty ? unavailableReason ?? undefined : undefined}
      className={`grid ${size === 'large' ? 'size-10' : 'size-9'} place-items-center overflow-hidden rounded-full border border-rule bg-bone text-[10px] text-gold-deep`}
      title={loading ? 'Загружаем файл…' : empty && unavailableReason ? unavailableReasonText(unavailableReason) : undefined}
    >
      {objectUrl ? <img src={objectUrl} alt={label} decoding="async" className="size-full object-cover" /> : loading ? '…' : '◇'}
    </span>
  )
}
